import { privateApiClient } from './apiClient';

/**
 * Service pour gérer les transactions
 */

/**
 * Récupérer les transactions avec filtres
 * @param {Object} filters - Filtres de recherche
 * @param {string} filters.own - 'all' ou 'org'
 * @param {string} filters.org - ID de l'organisation
 * @param {string} filters.sol - ID de la solution
 * @param {string} filters.ref - Référence
 * @param {string} filters.slug - Slug
 * @param {string} filters.crc - Devise (CDF/USD)
 * @param {string} filters.opr - Type d'opération
 * @param {string} filters.sts - Statut
 * @param {string} filters.fdt - Date début (format: DD.MM.YYYY)
 * @param {string} filters.tdt - Date fin (format: DD.MM.YYYY)
 * @param {number} filters.lmt - Limite par page
 * @param {number} filters.pge - Numéro de page
 * @returns {Promise<Object>} { contents: Array, nextPage: number|null }
 */
/**
 * Référence d'un paiement à afficher partout (reçu, Flow, notifications) :
 * code de la transaction du transaction-service (TXN-…-PMT… / -WDL…), commun au payeur et au marchand,
 * sinon référence du pos-service, sinon ref/id.
 */
export const displayReference = (t) => t?.code || t?.publicRef || t?.ref || t?.id || '—';

/** Nom du payeur à afficher : nom connu, sinon son numéro (paiement POS), sinon « Client ». */
export const displayCustomer = (t) =>
    t?.client_name || t?.pos?.payer_name || t?.pos?.payer_phone || t?.buyer?.full_name || t?.buyer?.account || 'Client';

/**
 * Retrouve un paiement POS par sa référence publique, l'ID imprimé sur le reçu ou son id POS.
 * @returns {Promise<Object|null>} TransactionRequest du pos-service, ou null si rien ne correspond
 */
export const lookupPayment = async (orgId, query) => {
    if (!orgId || !query?.trim()) return null;
    try {
        const { data } = await privateApiClient.get('/pos/transaction-requests/lookup', {
            params: { org: orgId, q: query.trim() }
        });
        return data?.error ? null : data;
    } catch (error) {
        return null;
    }
};

/** Associe à chaque transaction la référence publique du paiement POS (le transaction-service expose son id comme `ref`). */
const attachPaymentReferences = async (orgId, transactions) => {
    const refs = [...new Set(transactions.map((t) => t.ref).filter((r) => r && r !== 'all'))];
    if (!orgId || orgId === 'all' || refs.length === 0) return transactions;
    try {
        const { data } = await privateApiClient.get('/pos/transaction-requests/references', {
            params: { org: orgId, ids: refs.join(',') }
        });
        const byId = Object.fromEntries((Array.isArray(data) ? data : []).map((r) => [r.id, r]));
        return transactions.map((t) => {
            const match = byId[t.ref];
            return match ? { ...t, publicRef: match.code, receiptId: match.trans_id, pos: match } : t;
        });
    } catch (error) {
        return transactions;
    }
};

export const fetchTransactions = async (filters = {}) => {
    // Sur mobile, 'own' est directement l'ID de l'organisation ou 'all'
    // Les paramètres sont: own, sol, srv, act, ref, opr, slug, crc, sts, lmt, pge
    const defaultFilters = {
        own: 'all',
        lmt: 20,
        pge: 1,
        srv: 'all',
        act: 'all',
        ref: 'all',
        opr: 'all',
        slug: '',
        crc: 'all',
        sts: 'all',
        ...filters
    };
    
    // Si own est 'org' et qu'on a un org, utiliser l'ID de l'organisation (compatibilité)
    if (defaultFilters.own === 'org') {
        if (defaultFilters.org) {
            defaultFilters.own = defaultFilters.org;
            delete defaultFilters.org;
        } else {
            defaultFilters.own = 'all';
        }
    }
    
    // Nettoyer seulement undefined et null (garder les chaînes vides comme dans le mobile)
    // Le mobile envoie slug même s'il est vide
    Object.keys(defaultFilters).forEach(key => {
        if (defaultFilters[key] === undefined || defaultFilters[key] === null) {
            delete defaultFilters[key];
        }
    });
    
    // Recherche par référence (reçu, NK-…, id POS) : le transaction-service ignore `slug`,
    // on résout donc le paiement côté POS puis on filtre exactement sur sa `ref`, sans période ni devise.
    let matchedPayment = null;
    if (defaultFilters.slug && defaultFilters.own !== 'all') {
        matchedPayment = await lookupPayment(defaultFilters.own, defaultFilters.slug);
        if (matchedPayment?.id) {
            defaultFilters.ref = matchedPayment.id;
            defaultFilters.crc = 'all';
            defaultFilters.sts = 'all';
            defaultFilters.pge = 1;
            delete defaultFilters.fdt;
            delete defaultFilters.tdt;
        }
    }

    const { data: response } = await privateApiClient.get('/trs/transactions', {
        params: defaultFilters
    });

    const contents = await attachPaymentReferences(defaultFilters.own, response.contents || []);

    return {
        matchedPayment,
        contents,
        nextPage: response.nextPage || null,
        previousPage: response.previousPage || null,
        totalContents: response.totalContents || 0
    };
};

/**
 * Récupérer les transactions d'un POS spécifique
 * Le backend trs n'a pas d'endpoint cash-registers ; on utilise /pos/payment-terminals/{id}
 * pour récupérer le manager_id (caissier) du terminal et filtrer les transactions par act.
 * @param {string} posId - ID du terminal (payment-terminal)
 * @param {Object} filters - Filtres additionnels
 * @returns {Promise<Object>} Transactions du POS
 */
export const fetchPosTransactions = async (posId, filters = {}) => {
    let cashierId = null;
    try {
        const { data: terminalData } = await privateApiClient.get(`/pos/payment-terminals/${posId}`);
        cashierId = terminalData?.manager_id ?? terminalData?.manager ?? null;
    } catch (error) {
        // Terminal introuvable, continuer sans filtre act
    }
    return fetchTransactions({
        ...filters,
        ...(cashierId ? { act: cashierId } : {})
    });
};

/**
 * Récupérer les transactions d'un caissier spécifique
 * @param {string} cashierId - ID du caissier (numéro de téléphone)
 * @param {Object} filters - Filtres additionnels
 * @returns {Promise<Object>} Transactions du caissier
 */
export const fetchCashierTransactions = async (cashierId, filters = {}) => {
    return fetchTransactions({
        ...filters,
        ...(cashierId ? { act: cashierId } : {}),
    });
};

/**
 * Formater une date pour l'API (DD.MM.YYYY)
 * @param {Date} date - Date à formater
 * @returns {string} Date formatée
 */
export const formatDateForAPI = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

/**
 * Parser une date depuis l'API (DD.MM.YYYY)
 * @param {string} dateStr - Date formatée
 * @returns {Date|null} Date parsée ou null
 */
export const parseDateFromAPI = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('.');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
};

/**
 * Récupérer les statistiques du dashboard
 * @param {Object} params - Paramètres { own, sol, srv, act, crc, pvd }
 * @returns {Promise<Object>} Statistiques du dashboard
 */
export const fetchDashboardStats = async (params = {}) => {
    const defaultParams = {
        own: 'all',
        srv: 'all',
        act: 'all',
        crc: 'all',
        pvd: 'all',
        ...params
    };
    
    // Nettoyer les paramètres vides
    Object.keys(defaultParams).forEach(key => {
        if (defaultParams[key] === undefined || defaultParams[key] === null || defaultParams[key] === '') {
            delete defaultParams[key];
        }
    });
    
    const { data } = await privateApiClient.get('/trs/transactions/dashboard', {
        params: defaultParams
    });
    
    return data || {
        yearlyAmount: 0,
        lastYearAmount: 0,
        monthlyAmount: 0,
        lastMonthAmount: 0,
        dailyAmount: 0,
        lastDayAmount: 0
    };
};